/** Schedule slot grid step and duration dropdown options (New Schedule + encounter schedule). */
export const SCHEDULE_SLOT_DURATION_OPTIONS: ReadonlyArray<{
  displayName: string;
  value: string;
}> = [
  { displayName: "10 Mins", value: "10" },
  { displayName: "15 Mins", value: "15" },
  { displayName: "20 Mins", value: "20" },
  { displayName: "25 Mins", value: "25" },
  { displayName: "30 Mins", value: "30" },
  { displayName: "45 Mins", value: "45" },
  { displayName: "60 Mins", value: "60" },
  { displayName: "90 Mins", value: "90" },
  { displayName: "120 Mins", value: "120" },
  { displayName: "180 Mins", value: "180" },
];

/** Default duration when opening New Schedule (minutes as dropdown value). */
export const DEFAULT_SCHEDULE_SLOT_DURATION_MINUTES = "15";

/** `list-clinic` encounter statuses for New Schedule sheet timeline/sidebar loads. */
export const SCHEDULE_SHEET_LIST_CLINIC_FILTER_STATUS: readonly string[] = [
  "Confirmed",
  "Scheduled",
  "Complete",
  "Completed",
  "Ready for Billing",
  "Unconfirmed",
  "Open",
  "Waitlist",
  "Submitted",
  "Unsubmitted",
  "Sign Req",
];

export interface SchedulePeriodSlots {
  morning: string[];
  afternoon: string[];
  evening: string[];
}

/** Parses selected duration string; returns 0 until a valid duration is chosen. */
export function getScheduleSlotGridStepMinutes(
  selectedDurationMinutes: string
): number {
  const n = parseInt(selectedDurationMinutes, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function mapMinutesToScheduleDurationOption(minutes: number): string {
  const allowed = SCHEDULE_SLOT_DURATION_OPTIONS.map((o) =>
    parseInt(o.value, 10)
  );
  const m = Math.round(minutes);
  if (allowed.includes(m)) {
    return String(m);
  }
  return String(
    allowed.reduce((best, v) =>
      Math.abs(v - m) < Math.abs(best - m) ? v : best
    )
  );
}

export function generateScheduleAvailableSlots(
  startH: number,
  startM: number,
  endH: number,
  endM: number,
  incrementMinutes: number
): string[] {
  const step = Math.max(1, Math.floor(incrementMinutes));
  const slots: string[] = [];
  let h = startH;
  let m = startM;
  const endTotalMins = endH * 60 + endM;
  while (h * 60 + m <= endTotalMins) {
    const period = h >= 12 ? "PM" : "AM";
    const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
    slots.push(`${displayH}:${String(m).padStart(2, "0")} ${period}`);
    m += step;
    while (m >= 60) {
      m -= 60;
      h++;
    }
  }
  return slots;
}

/** Fixed 15-minute time options for New Schedule time dropdown (5:00 AM–11:45 PM). */
export const SCHEDULE_TIME_DROPDOWN_OPTIONS: readonly string[] =
  generateScheduleAvailableSlots(5, 0, 23, 45, 15);

/** Fixed 15-minute time options for schedule sheet Time dropdown (5:00 AM–10:45 PM, matches schedule calendar). */
export const SCHEDULE_SHEET_TIME_DROPDOWN_OPTIONS: readonly string[] =
  generateScheduleAvailableSlots(5, 0, 22, 45, 15);

function parseScheduleTimeLabelToMinutes(label: string): number | null {
  const trimmed = (label || "").trim();
  if (!trimmed) {
    return null;
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return null;
  }
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === "PM" && hour !== 12) {
    hour += 12;
  }
  if (period === "AM" && hour === 12) {
    hour = 0;
  }
  return hour * 60 + minute;
}

/** Map a calendar/sheet time label to a valid schedule sheet dropdown value. */
export function resolveScheduleSheetTimeDropdownValue(
  label: string,
  options: readonly string[] = SCHEDULE_SHEET_TIME_DROPDOWN_OPTIONS
): string | null {
  const trimmed = (label || "").trim();
  if (!trimmed) {
    return null;
  }
  const exact = options.find(
    (opt) => opt.toLowerCase() === trimmed.toLowerCase()
  );
  if (exact) {
    return exact;
  }
  const targetMinutes = parseScheduleTimeLabelToMinutes(trimmed);
  if (targetMinutes == null) {
    return null;
  }
  let best: string | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const opt of options) {
    const optMinutes = parseScheduleTimeLabelToMinutes(opt);
    if (optMinutes == null) {
      continue;
    }
    const delta = Math.abs(optMinutes - targetMinutes);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = opt;
    }
  }
  return best;
}

/** Morning / afternoon / evening windows aligned with New Schedule modal. */
export function buildSchedulePeriodSlots(
  stepMinutes: number
): SchedulePeriodSlots {
  if (stepMinutes <= 0) {
    return { morning: [], afternoon: [], evening: [] };
  }
  return {
    morning: generateScheduleAvailableSlots(5, 0, 10, 45, stepMinutes),
    afternoon: generateScheduleAvailableSlots(11, 0, 13, 45, stepMinutes),
    evening: generateScheduleAvailableSlots(14, 0, 16, 45, stepMinutes),
  };
}

/** Joins schedule sheet header segments; unset values are omitted (not shown as N/A). */
export function formatScheduleHeaderSubtitle(
  parts: Array<string | null | undefined>
): string {
  return parts
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" • ");
}
